/**
 * Bot Farm — every enrolled bot plus the master control panel.
 *
 * Select any number of bots (or "all active") and send one action to all of
 * them: pause / resume trading, restart the bot process, reboot the whole
 * Raspberry Pi, or apply a trading config. Clicking a bot opens its own
 * dashboard (stats, assignments, per-bot controls). The hierarchical pause
 * policies that used to be the "Pause Controls" page live in the collapsible
 * "Advanced pause policies" section here.
 *
 * Live updates via the CC SSE stream; /fleet/enroll renders this page with
 * the enroll modal pre-opened.
 */
import { useMemo, useState } from "react";
import {
  Accordion, Alert, Badge, Button, Checkbox, Grid, Group, Modal, Paper, Stack, Text, Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconPlayerPause, IconPlayerPlay, IconPlus, IconPower, IconRefresh, IconRotateClockwise, IconSettings,
} from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { fleetApi, type Bot } from "../../api/fleet";
import { AgentCard } from "../../components/fleet/AgentCard";
import { EnrollModal } from "../../components/fleet/EnrollModal";
import { useAuth } from "../../hooks/useAuth";
import { useSSE } from "../../hooks/useSSE";
import { ControlsPage } from "./Controls";
import { ApplyConfigModal } from "./FleetAssignments";

type BulkVerb = "pause" | "resume" | "restart" | "reboot";

const BULK_LABEL: Record<BulkVerb, { title: string; done: string; danger: boolean }> = {
  pause: { title: "Pause trading", done: "paused", danger: false },
  resume: { title: "Resume trading", done: "resumed", danger: false },
  restart: { title: "Restart bot software", done: "restarting", danger: true },
  reboot: { title: "Reboot Raspberry Pi", done: "rebooting", danger: true },
};

const isActive = (b: Bot) => b.status === "online" || b.status === "paused";

export function FleetPage({ enrollDefaultOpen = false }: { enrollDefaultOpen?: boolean }) {
  const qc = useQueryClient();
  const { isAdmin } = useAuth();
  const [enrollOpen, setEnrollOpen] = useState(enrollDefaultOpen);
  const [selected, setSelected] = useState<string[]>([]);
  const [confirm, setConfirm] = useState<BulkVerb | null>(null);
  const [applyOpen, setApplyOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const botsQ = useQuery({
    queryKey: ["fleet", "agents"],
    queryFn: () => fleetApi.agents.list(),
    refetchInterval: 15_000,
  });
  const bots = useMemo(() => (botsQ.data ?? []).filter((b) => b.status !== "disabled"), [botsQ.data]);
  const activeIds = useMemo(() => bots.filter(isActive).map((b) => b.id), [bots]);
  const selectedBots = bots.filter((b) => selected.includes(b.id));

  // Realtime updates: bot.status / bot.stats / agent.health events
  // invalidate the ["fleet","agents"] query inside useSSE.
  useSSE({});

  const toggle = (id: string, on: boolean) =>
    setSelected((prev) => (on ? [...new Set([...prev, id])] : prev.filter((x) => x !== id)));
  const allActiveSelected = activeIds.length > 0 && activeIds.every((id) => selected.includes(id));

  async function runBulk(verb: BulkVerb) {
    setBusy(true);
    const targets = selectedBots;
    // Pause/resume drive the bot-level cloud policy (what the Pi enforces), so
    // Resume always lifts what Pause set; restart/reboot stay plain commands.
    const results = await Promise.allSettled(
      targets.map((b) =>
        verb === "pause" ? fleetApi.fleet.pauseBot(b.id)
          : verb === "resume" ? fleetApi.fleet.resumeBot(b.id)
            : fleetApi.commands.issue({ agent_id: b.id, verb, ttl_seconds: 300 })),
    );
    setBusy(false);
    setConfirm(null);
    const failed = results.flatMap((r, i) => (r.status === "rejected" ? [targets[i]!.display_name] : []));
    void qc.invalidateQueries({ queryKey: ["fleet"] });
    notifications.show({
      color: failed.length ? "red" : "green",
      title: `${BULK_LABEL[verb].title}: sent to ${targets.length - failed.length} of ${targets.length}`,
      message: failed.length
        ? `Could not queue for: ${failed.join(", ")}`
        : `Each bot confirms on its own; offline bots receive it when they reconnect (5 min).`,
    });
  }

  return (
    <>
      <Group justify="space-between" mb="lg">
        <Title order={2}>Bot Farm</Title>
        <Group>
          <Button
            variant="subtle"
            leftSection={<IconRefresh size={16} />}
            onClick={() => void botsQ.refetch()}
          >
            Refresh
          </Button>
          {isAdmin && (
            <Button leftSection={<IconPlus size={16} />} onClick={() => setEnrollOpen(true)}>
              Enroll Bot
            </Button>
          )}
        </Group>
      </Group>

      {isAdmin && bots.length > 0 && (
        <Paper withBorder p="md" mb="lg">
          <Group justify="space-between" mb="sm" wrap="wrap">
            <div>
              <Text fw={700}>Master controls</Text>
              <Text size="sm" c="dimmed">
                Pick bots below (or use the shortcuts), then send one action to all of them.
              </Text>
            </div>
            <Group gap="xs">
              <Checkbox
                label={`All active (${activeIds.length})`}
                checked={allActiveSelected}
                indeterminate={!allActiveSelected && selected.length > 0}
                onChange={(e) => setSelected(e.currentTarget.checked ? activeIds : [])}
              />
              <Button size="compact-sm" variant="subtle" onClick={() => setSelected(bots.map((b) => b.id))}>
                Select all
              </Button>
              <Button size="compact-sm" variant="subtle" color="gray" onClick={() => setSelected([])} disabled={!selected.length}>
                Clear
              </Button>
            </Group>
          </Group>
          <Group gap="xs" wrap="wrap">
            <Badge variant="light" size="lg">{selected.length} selected</Badge>
            <Button size="sm" variant="light" color="yellow" leftSection={<IconPlayerPause size={16} />}
              disabled={!selected.length} onClick={() => setConfirm("pause")}>Pause</Button>
            <Button size="sm" variant="light" color="green" leftSection={<IconPlayerPlay size={16} />}
              disabled={!selected.length} onClick={() => setConfirm("resume")}>Resume</Button>
            <Button size="sm" variant="light" leftSection={<IconSettings size={16} />}
              disabled={!selected.length} onClick={() => setApplyOpen(true)}>Apply config</Button>
            <Button size="sm" variant="light" color="orange" leftSection={<IconRotateClockwise size={16} />}
              disabled={!selected.length} onClick={() => setConfirm("restart")}>Restart bot</Button>
            <Button size="sm" variant="light" color="red" leftSection={<IconPower size={16} />}
              disabled={!selected.length} onClick={() => setConfirm("reboot")}>Reboot Pi</Button>
          </Group>
        </Paper>
      )}

      {botsQ.error != null && (
        <Alert color="red" mb="md">Failed to load bots.</Alert>
      )}

      {botsQ.isLoading ? (
        <Text c="dimmed">Loading…</Text>
      ) : !bots.length ? (
        <Stack align="center" mt="xl">
          <Text size="lg" c="dimmed">No bots enrolled yet.</Text>
          {isAdmin && <Button onClick={() => setEnrollOpen(true)}>Enroll your first bot</Button>}
        </Stack>
      ) : (
        <Grid>
          {bots.map((bot) => (
            <Grid.Col key={bot.id} span={{ base: 12, sm: 6, lg: 4 }}>
              <AgentCard
                agent={bot}
                selected={selected.includes(bot.id)}
                onSelectedChange={isAdmin ? (on) => toggle(bot.id, on) : undefined}
              />
            </Grid.Col>
          ))}
        </Grid>
      )}

      {isAdmin && (
        <Accordion variant="separated" mt="xl">
          <Accordion.Item value="policies">
            <Accordion.Control>
              <Text fw={600}>Advanced pause policies</Text>
              <Text size="xs" c="dimmed">Pause a whole venue, category, market family, subscription or the entire farm — and watch each bot confirm.</Text>
            </Accordion.Control>
            <Accordion.Panel>
              <ControlsPage embedded />
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      )}

      <Modal
        opened={confirm != null}
        onClose={() => setConfirm(null)}
        title={confirm ? `${BULK_LABEL[confirm].title} — ${selectedBots.length} bot(s)` : ""}
      >
        {confirm && (
          <Stack>
            <Text size="sm">
              {confirm === "reboot"
                ? "The whole Raspberry Pi restarts. The bot is offline for about a minute and comes back on its own."
                : confirm === "restart"
                  ? "The bot software restarts (a few seconds offline). Open positions are kept."
                  : confirm === "pause"
                    ? "New buys and sells stop on these bots. Data collection keeps running."
                    : "These bots start trading again (unless a higher-level pause policy still applies)."}
            </Text>
            <Text size="xs" c="dimmed">{selectedBots.map((b) => b.display_name).join(", ")}</Text>
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setConfirm(null)}>Cancel</Button>
              <Button color={BULK_LABEL[confirm].danger ? "red" : undefined} loading={busy} onClick={() => void runBulk(confirm)}>
                {BULK_LABEL[confirm].title}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      <ApplyConfigModal opened={applyOpen} onClose={() => setApplyOpen(false)} initialAgentIds={selected} />
      <EnrollModal opened={enrollOpen} onClose={() => setEnrollOpen(false)} />
    </>
  );
}
